import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ArrowLeft, Loader2, Upload, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { inputCls, labelCls } from "../../features/web3/forex/components/FormDialog";
import RichTextEditor from "./RichTextEditor";
import {
  IMAGE_ACCEPT,
  STATUS_OPTIONS,
  mediaUrl,
  uploadBlogImage,
  useBlog,
  useBlogTaxonomy,
  useSaveBlog,
} from "./api";

const EMPTY = {
  title: "",
  slug: "",
  summary: "",
  coverImage: "",
  category: "",
  tags: "",
  author: "",
  status: "published",
  isFeatured: false,
  publishedAt: "",
  content: "",
};

const toForm = (b) => ({
  title: b.title,
  slug: b.slug,
  summary: b.summary,
  coverImage: b.coverImage,
  category: b.category,
  tags: (b.tags || []).join(", "),
  author: b.author,
  status: b.status,
  isFeatured: b.isFeatured,
  // Kept as the stored ISO until the date is changed, so an untouched date keeps its time.
  publishedAt: b.publishedAt || "",
  content: b.content,
});

export default function BlogForm() {
  const { id } = useParams();
  const blog = useBlog(id);

  return (
    <QueueLayout
      eyebrow="Blog"
      title={id ? "Edit post" : "New post"}
      isFetching={blog.isFetching}
      onRefresh={() => id && blog.refetch()}
    >
      <Link to="/blogs" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" />
        All posts
      </Link>
      {!id ? (
        <PostForm initial={EMPTY} />
      ) : blog.error ? (
        <ErrorBanner error={blog.error} onRetry={() => blog.refetch()} />
      ) : blog.data ? (
        // Keyed so the editor starts from the loaded content.
        <PostForm key={blog.data._id} id={blog.data._id} initial={toForm(blog.data)} />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      )}
    </QueueLayout>
  );
}

function PostForm({ id, initial }) {
  const navigate = useNavigate();
  const taxonomy = useBlogTaxonomy();
  const save = useSaveBlog();
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadBlogImage(file);
      setForm((f) => ({ ...f, coverImage: path }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Image upload failed");
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const { publishedAt, ...rest } = form;
    try {
      await save.mutateAsync({
        id,
        ...rest,
        // Sent only when set. Edit applies publishedAt after status, so an empty one would
        // wipe the date the backend stamps when a draft is published.
        ...(publishedAt ? { publishedAt } : {}),
      });
      toast.success(id ? "Post updated" : "Post created");
      navigate("/blogs");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    }
  };

  const canSave = form.title.trim() && form.content.trim() && !uploading;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_340px]">
      <div className="space-y-5">
        <div>
          <label className={labelCls}>Title</label>
          <input value={form.title} maxLength={300} onChange={set("title")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Slug</label>
          <input
            value={form.slug}
            onChange={set("slug")}
            placeholder="Leave empty to build it from the title"
            className={`${inputCls} font-mono`}
          />
        </div>
        <div>
          <label className={labelCls}>Summary</label>
          <Textarea
            rows={3}
            value={form.summary}
            onChange={set("summary")}
            placeholder={id ? "" : "Leave empty to use the start of the content"}
          />
        </div>
        <div>
          <label className={labelCls}>Content</label>
          <RichTextEditor value={form.content} onChange={(content) => setForm((f) => ({ ...f, content }))} />
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className={labelCls}>Cover image</label>
          {form.coverImage ? (
            <div className="relative">
              <img src={mediaUrl(form.coverImage)} alt="" className="aspect-video w-full rounded-md border border-border object-cover" />
              <button
                type="button"
                title="Remove cover"
                onClick={() => setForm((f) => ({ ...f, coverImage: "" }))}
                className="absolute right-2 top-2 rounded-full bg-background/80 p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:border-ginox-green/50 hover:text-foreground">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              {uploading ? "Uploading…" : "JPG, PNG, WEBP or GIF, up to 10 MB"}
              <input type="file" accept={IMAGE_ACCEPT} className="hidden" disabled={uploading} onChange={uploadCover} />
            </label>
          )}
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={form.status} onChange={set("status")} className={inputCls}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Published date</label>
          <input
            type="date"
            // The stored ISO is UTC; show the admin's own calendar day.
            value={form.publishedAt.length > 10 ? format(new Date(form.publishedAt), "yyyy-MM-dd") : form.publishedAt}
            onChange={set("publishedAt")}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <input
            list="blog-categories"
            value={form.category}
            maxLength={100}
            placeholder="General"
            onChange={set("category")}
            className={inputCls}
          />
          <datalist id="blog-categories">
            {taxonomy.data?.categories?.map((c) => (
              <option key={c.category} value={c.category} />
            ))}
          </datalist>
        </div>
        <div>
          <label className={labelCls}>Tags</label>
          <input value={form.tags} onChange={set("tags")} placeholder="forex, trading" className={inputCls} />
          <p className="mt-1 text-[11px] text-muted-foreground/70">Separate with commas.</p>
        </div>
        <div>
          <label className={labelCls}>Author</label>
          <input value={form.author} onChange={set("author")} placeholder="Ginox Team" className={inputCls} />
        </div>
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={form.isFeatured}
            onCheckedChange={(v) => setForm((f) => ({ ...f, isFeatured: v }))}
          />
          Featured
        </label>
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={!canSave || save.isPending}>
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {id ? "Save" : "Create post"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/blogs")}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}
